import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Shirt, Plus, RefreshCw, Search, Loader, Trash2, X, Save, RotateCcw, CalendarDays, AlertTriangle, MapPin, Upload } from 'lucide-react';

// Uniform Register — every AP uniform order placed for a driver, with its line items
// and sizes. AP's supply-chain portal gives back an order reference and nothing else,
// so the record of who was kitted out, when, in what size and at what cost used to be
// a confirmation screenshot. Backed by uniform-orders-sb (settings KV 'uniform_orders').
//
// The Personnel profile shows the LATEST order per driver; this is the history behind
// it, and saving here keeps that mirror in step.

const URL = '/.netlify/functions/uniform-orders-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '10px 12px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
const money = (n) => (n == null || n === '' ? '—' : (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }));
const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : 0; };
const lineTotal = (it) => num(it.unit_price) * (num(it.qty) || 1);
const orderTotal = (items) => (items || []).reduce((t, it) => t + lineTotal(it), 0);

// AP's portal offers two ship-to addresses and only one of them is ours. Orders have
// gone to the wrong one, and the AP confirmation shows the address in a box you scroll
// past — so the register records it per order, defaults to the right one, and says so
// loudly when the wrong one is picked. Free text is still allowed for anything new.
export const DELIVERY_ADDRESSES = [
  { value: '1A Hale Street, Botany', ok: true,  hint: 'Correct — our depot' },
  { value: '10-12 Ralph Street, Alexandria', ok: false, hint: 'Wrong — orders sent here do not reach us' },
];
// AP prints an address as "10-12 Ralph Street ALEXANDRIA 2015 AU NSW" — no comma, caps
// suburb, postcode and state tacked on — so an exact string match would call every real
// order "unrecognised". Compare on the street-and-suburb part with punctuation and case
// flattened, and let the trailing postcode/state ride along.
// AP prints the street type both ways — "1A Hale St BOTANY" on one order and
// "10-12 Ralph Street ALEXANDRIA" on the next — so the suffix is expanded before
// comparing. Without this, Thomas Nolan's order went to the right depot and was
// still flagged "not a known address".
const STREET_TYPES = {
  st: 'street', rd: 'road', ave: 'avenue', av: 'avenue', ln: 'lane', dr: 'drive',
  pde: 'parade', cres: 'crescent', cr: 'crescent', hwy: 'highway', pl: 'place',
  ct: 'court', tce: 'terrace', cl: 'close', esp: 'esplanade',
};
const normAddr = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  .split(' ').map((w) => STREET_TYPES[w] || w).join(' ');
export const matchedAddress = (addr) => {
  const hay = normAddr(addr);
  return hay ? (DELIVERY_ADDRESSES.find((a) => hay.includes(normAddr(a.value))) || null) : null;
};
export const addressStatus = (addr) => {
  if (!normAddr(addr)) return 'missing';
  const hit = matchedAddress(addr);
  return hit ? (hit.ok ? 'ok' : 'wrong') : 'unknown';
};
const CORRECT_ADDRESS = DELIVERY_ADDRESSES.find((a) => a.ok).value;

const emptyItem = () => ({ product: '', product_id: '', product_code: '', size: '', qty: 1, unit_price: '' });
const emptyForm = () => ({ id: '', driver_login: '', staff_type: 'driver', order_date: todayISO(), order_ref: '', supplier: 'DHL(VIC) Supply Chain', delivery_address: CORRECT_ADDRESS, sales_order: '', approver: '', note: '', items: [emptyItem()] });

// Sizes on file, derived rather than stored: for each garment a driver has been sent,
// the size on their MOST RECENT order of it. Deriving means the sizes can never
// disagree with the orders — correct an order and the size follows.
export function sizesOnFile(orders, login, staffType) {
  const mine = (orders || [])
    .filter((o) => (o.driver_login || '').toUpperCase() === (login || '').toUpperCase()
      && (o.staff_type || 'driver') === (staffType || 'driver'))
    .sort((a, b) => (a.order_date || '').localeCompare(b.order_date || ''));   // oldest first, so later wins
  const sizes = {};
  for (const o of mine) {
    for (const it of (o.items || [])) {
      if (it.product && it.size) sizes[it.product] = { size: it.size, product_id: it.product_id || '', product_code: it.product_code || '', when: o.order_date };
    }
  }
  return sizes;
}

// Every product ordered before, newest first — the datalist behind the product box, so
// the second order for an item never needs its name or AP Product ID typed again.
function catalogue(orders) {
  const seen = new Map();
  for (const o of [...(orders || [])].sort((a, b) => (b.order_date || '').localeCompare(a.order_date || ''))) {
    for (const it of (o.items || [])) {
      if (it.product && !seen.has(it.product)) seen.set(it.product, it);
    }
  }
  return [...seen.values()];
}

// AP's Order Status Report has no export and no print worth the name, so an order
// reaches us as screenshots. Drop them here and the particulars are read off them.
const MAX_MB = 4;   // base64 inflates ~33%; Netlify's function body limit is 6 MB
async function fileToImage(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return { base64: btoa(bin), mediaType: file.type || 'image/png', name: file.name, mb: file.size / 1e6 };
}

export default function UniformRegisterPage() {
  const { addNotification } = useApp();
  const [orders, setOrders] = useState([]);
  // Drivers AND night sorters — two separate registries, one picker. A login is only
  // unique within its own table, so every order records which register it came from.
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);         // null = drawer closed
  const [undo, setUndo] = useState(null);         // last deleted order, for Undo
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [dropping, setDropping] = useState(false);   // drag hovering the zone
  const [reading, setReading] = useState(false);     // OCR in flight
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('list');
      if (res.success) setOrders(res.orders || []);
      else addNotification(res.error || 'Could not load the uniform register', 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setLoading(false); }
  }, [addNotification]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('people').then((r) => { if (r.success) setPeople(r.people || []); }).catch(() => {});
  }, []);

  const personOf = useCallback((login, staffType) => (people || []).find(
    (p) => p.login === (login || '').toUpperCase() && (!staffType || p.staff_type === staffType),
  ) || (people || []).find((p) => p.login === (login || '').toUpperCase()), [people]);
  const nameOf = useCallback((login, staffType) => (personOf(login, staffType) || {}).name || login, [personOf]);

  const cat = useMemo(() => catalogue(orders), [orders]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => [
      o.driver_login, nameOf(o.driver_login, o.staff_type), o.order_ref, o.note, o.delivery_address, o.staff_type,
      ...(o.items || []).map((i) => `${i.product} ${i.product_id} ${i.size}`),
    ].join(' ').toLowerCase().includes(needle));
  }, [orders, q, nameOf]);

  // Spend this financial year (1 Jul) — the number anyone asks for at tax time.
  const fyTotal = useMemo(() => {
    const now = new Date();
    const fyStart = `${now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1}-07-01`;
    return orders.filter((o) => (o.order_date || '') >= fyStart).reduce((t, o) => t + num(o.total), 0);
  }, [orders]);

  // Orders that went to the wrong depot — the number that says whether this is still
  // happening, sitting where it cannot be missed rather than buried in the rows.
  const misSent = useMemo(() => orders.filter((o) => addressStatus(o.delivery_address) === 'wrong'), [orders]);

  const openNew = (login = '', staffType = 'driver') => {
    const f = emptyForm();
    f.driver_login = login;
    f.staff_type = staffType;
    // Kit them out in what they had last time: their sizes carry forward, so a reorder is
    // a couple of clicks instead of a hunt through old confirmations.
    const sizes = sizesOnFile(orders, login, staffType);
    const prior = Object.entries(sizes);
    // AP issues a DIFFERENT product ID per size of the same garment (Cargo Shorts Mas
    // Olive is 8845331 in 82 and 8845332 in 87), so a size carried forward and then
    // changed leaves the ID pointing at the old size. Remember what the ID was filled
    // against, so the drawer can say so — a quietly wrong ID is worse than none.
    if (prior.length) f.items = prior.map(([product, s]) => ({ product, product_id: s.product_id, product_code: s.product_code || '', size: s.size, size_at_fill: s.size, qty: 1, unit_price: '' }));
    setForm(f);
  };
  const openEdit = (o) => setForm({ ...o, items: (o.items || []).map((i) => ({ ...i })) });

  // Drop the order's screenshots (all of them at once — a full order is two or three
  // scrolls) and the drawer opens filled in, for review. Nothing saves on its own.
  const readFiles = useCallback(async (fileList) => {
    const files = [...fileList].filter((f) => f.type.startsWith('image/'));
    if (!files.length) { addNotification('Drop image screenshots of the AP order', 'error'); return; }
    if (files.length > 5) { addNotification('Five screenshots at most', 'error'); return; }
    setReading(true);
    try {
      const images = await Promise.all(files.map(fileToImage));
      const tooBig = images.find((i) => i.mb > MAX_MB);
      if (tooBig) { addNotification(`${tooBig.name} is ${tooBig.mb.toFixed(1)} MB — keep each under ${MAX_MB} MB`, 'error'); return; }
      const res = await api('parseOrder', { images: images.map(({ base64, mediaType }) => ({ base64, mediaType })) });
      if (!res.success) { addNotification(res.error || 'Could not read the order', 'error'); return; }
      const d = res.draft;
      if (res.warning) addNotification(res.warning, 'warning');
      if (!d.driver_login && d.driver_name_read) addNotification(`Read the order for "${d.driver_name_read}" — pick the driver to match it to`, 'info');
      setForm({
        id: '', driver_login: d.driver_login || '', staff_type: d.staff_type || 'driver', order_date: d.order_date || todayISO(),
        order_ref: d.order_ref || '', supplier: d.supplier || 'DHL(VIC) Supply Chain',
        delivery_address: d.delivery_address || '', sales_order: d.sales_order || '', approver: d.approver || '',
        note: '', items: d.items && d.items.length ? d.items.map((i) => ({ ...i, unit_price: i.unit_price || '' })) : [emptyItem()],
      });
      addNotification(`Read ${d.items.length} line${d.items.length === 1 ? '' : 's'} from ${files.length} screenshot${files.length === 1 ? '' : 's'} — check it and save`, 'success');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setReading(false); }
  }, [addNotification]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, n) => (n === i ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const dropItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, n) => n !== i) }));

  // Picking a known product fills its AP Product ID and last size — the two things that
  // are tedious to re-key and easy to get wrong.
  const pickProduct = (i, product) => {
    const hit = cat.find((c) => c.product === product);
    setForm((f) => ({
      ...f,
      items: f.items.map((it, n) => (n === i
        ? { ...it, product, product_id: hit && !it.product_id ? hit.product_id : it.product_id, size: hit && !it.size ? hit.size : it.size }
        : it)),
    }));
  };

  const save = async () => {
    if (!form.driver_login) { addNotification('Pick a driver', 'error'); return; }
    if (!form.items.some((i) => i.product.trim())) { addNotification('Add at least one item', 'error'); return; }
    setSaving(true);
    try {
      const res = await api(form.id ? 'update' : 'create', { ...form, items: form.items.filter((i) => i.product.trim()) });
      if (res.success) {
        if (res.warning) addNotification(res.warning, 'warning');
        addNotification(`Uniform order ${form.id ? 'updated' : 'recorded'} for ${nameOf(form.driver_login, form.staff_type)}`, 'success');
        setForm(null);
        load();
      } else addNotification(res.error || 'Save failed', 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setSaving(false); }
  };

  // Delete keeps the row in hand and offers it back — a wrong click shouldn't cost the record.
  const remove = async (o) => {
    if (!window.confirm(`Delete the ${fmtDate(o.order_date)} order for ${nameOf(o.driver_login, o.staff_type)}?`)) return;
    const res = await api('remove', { id: o.id });
    if (res.success) { setUndo(res.removed); load(); addNotification('Order deleted', 'info'); }
    else addNotification(res.error || 'Delete failed', 'error');
  };
  const restore = async () => {
    const res = await api('restore', { order: undo });
    if (res.success) { setUndo(null); load(); addNotification('Order restored', 'success'); }
    else addNotification(res.error || 'Restore failed', 'error');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><Shirt size={22} /> Uniform Register</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}><RefreshCw size={13} /> Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={() => openNew()}><Plus size={13} /> Record an order</button>
        </div>
      </div>
      <p style={{ margin: '0 0 18px', color: 'var(--hp-dim)', fontSize: '.85rem', maxWidth: 760, lineHeight: 1.55 }}>
        Every uniform order placed on AP's supply-chain portal, with its reference, line items and sizes.
        The sizes here are what each driver was last sent, so a reorder starts pre-filled instead of guessed.
        {orders.length > 0 && <> {orders.length} order{orders.length === 1 ? '' : 's'} on file · {money(fyTotal)} this financial year.</>}
      </p>

      {misSent.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', marginBottom: 14, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.4)', borderRadius: 8 }}>
          <AlertTriangle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '.83rem', lineHeight: 1.5 }}>
            <b>{misSent.length} order{misSent.length === 1 ? ' was' : 's were'} sent to the wrong address.</b>{' '}
            {misSent.map((o) => `${nameOf(o.driver_login, o.staff_type)} (${fmtDate(o.order_date)})`).join(', ')}.
            {' '}Uniforms shipped to {DELIVERY_ADDRESSES.find((a) => !a.ok).value} don't reach us — the correct address is <b>{CORRECT_ADDRESS}</b>.
          </div>
        </div>
      )}

      {undo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 14, background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.35)', borderRadius: 8 }}>
          <span style={{ fontSize: '.82rem' }}>Deleted the {fmtDate(undo.order_date)} order for <b>{nameOf(undo.driver_login, undo.staff_type)}</b>.</span>
          <button className="btn btn-secondary btn-sm" onClick={restore}><RotateCcw size={13} /> Undo</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setUndo(null)}><X size={13} /></button>
        </div>
      )}

      {/* Drop zone — the whole point is that an order arrives as screenshots, so this is
          the primary way in and the manual form is the fallback, not the other way round. */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDropping(true); }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => { e.preventDefault(); setDropping(false); readFiles(e.dataTransfer.files); }}
        onClick={() => !reading && fileRef.current && fileRef.current.click()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          padding: '18px 20px', marginBottom: 14, cursor: reading ? 'progress' : 'pointer',
          border: `1.5px dashed ${dropping ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10,
          background: dropping ? 'rgba(201,168,76,.08)' : 'transparent', transition: 'all .12s',
        }}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={(e) => { readFiles(e.target.files); e.target.value = ''; }} />
        {reading ? (
          <><Loader size={16} className="spin" /> <span style={{ fontSize: '.85rem' }}>Reading the order…</span></>
        ) : (
          <>
            <Upload size={16} style={{ color: dropping ? 'var(--hp-gold)' : 'var(--hp-muted)' }} />
            <span style={{ fontSize: '.85rem', color: 'var(--hp-dim)' }}>
              <b style={{ color: 'var(--hp-text)' }}>Drop the order screenshots here</b> — all of them at once — and the details are read off them. Or click to choose.
            </span>
          </>
        )}
      </div>

      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 14 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-muted)' }} />
        <input className="form-input" style={{ ...inputStyle, paddingLeft: 30 }} placeholder="Driver, reference, item or size…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-dim)' }}><Loader size={15} className="spin" /> Loading…</div>
      ) : !shown.length ? (
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--hp-muted)', border: '1px dashed var(--hp-border)', borderRadius: 10 }}>
          {orders.length ? 'No order matches that search.' : 'No uniform orders recorded yet. Record one after you submit it on the AP portal.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead><tr>
              <th style={th}>Driver</th><th style={th}>Ordered</th><th style={th}>AP Ref</th>
              <th style={th}>Items</th><th style={th}>Delivered to</th><th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th} />
            </tr></thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{nameOf(o.driver_login, o.staff_type)}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.68rem', color: 'var(--hp-dim)' }}>{o.driver_login}</span>
                      {o.staff_type === 'sorter' && <span style={{ fontSize: '.6rem', fontWeight: 800, letterSpacing: '.05em', color: '#7c3aed', border: '1px solid #7c3aed', borderRadius: 4, padding: '0 4px' }}>NIGHT SORT</span>}
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(o.order_date)}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}>{o.order_ref || '—'}</td>
                  <td style={td}>
                    {(o.items || []).map((it, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                        <span>{it.product}</span>
                        {it.size && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', color: 'var(--hp-gold)' }}>sz {it.size}</span>}
                        <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>×{it.qty}</span>
                      </div>
                    ))}
                    {o.note && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>{o.note}</div>}
                  </td>
                  <td style={td}><AddressCell address={o.delivery_address} /></td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{o.total ? money(o.total) : '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(o)} title="Edit this order">Edit</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openNew(o.driver_login, o.staff_type)} title="New order for this person, pre-filled with their sizes">Reorder</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(o)} title="Delete"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <OrderDrawer
          form={form} setField={setField} setItem={setItem} addItem={addItem} dropItem={dropItem}
          pickProduct={pickProduct} catalogue={cat} people={people} sizes={sizesOnFile(orders, form.driver_login, form.staff_type)}
          saving={saving} onSave={save} onClose={() => setForm(null)}
          onPersonChange={(login, staffType) => setForm((f) => ({ ...f, driver_login: login, staff_type: staffType }))}
        />
      )}
    </div>
  );
}

// How an order's ship-to reads in the table: right address stays quiet, wrong one is
// unmissable, anything unrecognised is called out rather than silently trusted.
function AddressCell({ address }) {
  const status = addressStatus(address);
  if (status === 'missing') return <span style={{ color: 'var(--hp-muted)' }}>—</span>;
  const look = {
    ok:      { color: 'var(--hp-dim)', label: '' },
    wrong:   { color: '#ef4444', label: 'WRONG ADDRESS' },
    unknown: { color: '#d97706', label: 'not a known address' },
  }[status];
  return (
    <div style={{ fontSize: '.8rem', color: look.color, whiteSpace: 'nowrap' }}>
      {address}
      {look.label && <div style={{ fontSize: '.65rem', fontWeight: 800, letterSpacing: '.04em' }}>{look.label}</div>}
    </div>
  );
}

function OrderDrawer({ form, setField, setItem, addItem, dropItem, pickProduct, catalogue: cat, people, sizes, saving, onSave, onClose, onPersonChange }) {
  const total = orderTotal(form.items);
  const priorSizes = Object.entries(sizes || {});
  const addrStatus = addressStatus(form.delivery_address);
  // Lines prefilled from a past order whose size has since been edited — their AP
  // Product ID still belongs to the old size.
  const resized = form.items.filter((i) => i.size_at_fill && i.product_id && i.size !== i.size_at_fill).map((i) => i.product);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 640, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>{form.id ? 'Edit uniform order' : 'Record a uniform order'}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save order'}</button>
          </div>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Person</label>
            {/* Drivers and night sorters both get uniforms, and their logins live in
                separate tables — so the value carries the register, not just the login. */}
            <select className="form-select" style={inputStyle}
              value={form.driver_login ? `${form.staff_type || 'driver'}:${form.driver_login}` : ''}
              onChange={(e) => { const [t, l] = e.target.value.split(':'); onPersonChange(l || '', t || 'driver'); }}>
              <option value="">Select a driver or sorter…</option>
              <optgroup label="Drivers">
                {(people || []).filter((p) => p.staff_type === 'driver')
                  .map((p) => <option key={`driver:${p.login}`} value={`driver:${p.login}`}>{p.name} ({p.login})</option>)}
              </optgroup>
              <optgroup label="Night Sort">
                {(people || []).filter((p) => p.staff_type === 'sorter')
                  .map((p) => <option key={`sorter:${p.login}`} value={`sorter:${p.login}`}>{p.name} ({p.login})</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Order date</label>
            <input className="form-input" style={inputStyle} type="date" value={form.order_date} onChange={(e) => setField('order_date', e.target.value)} />
          </div>
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 6 }}>
          <div>
            <label style={labelStyle}>AP order reference</label>
            <input className="form-input" style={inputStyle} placeholder="e.g. 14352082" value={form.order_ref} onChange={(e) => setField('order_ref', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Supplier</label>
            <input className="form-input" style={inputStyle} value={form.supplier} onChange={(e) => setField('supplier', e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 14 }}>
          The reference on AP's "Order submitted" confirmation.
        </div>

        {/* Ship-to. AP's portal offers two and it is easy to submit against the wrong one —
            this defaults to ours and objects out loud rather than recording it quietly. */}
        <label style={labelStyle}>Delivery address</label>
        <select className="form-select" style={{ ...inputStyle, marginBottom: 6 }}
          value={(matchedAddress(form.delivery_address) || {}).value || '__other'}
          onChange={(e) => setField('delivery_address', e.target.value === '__other' ? '' : e.target.value)}>
          {DELIVERY_ADDRESSES.map((a) => <option key={a.value} value={a.value}>{a.value} — {a.hint}</option>)}
          <option value="__other">Another address…</option>
        </select>
        {!matchedAddress(form.delivery_address) && (
          <input className="form-input" style={{ ...inputStyle, marginBottom: 6 }} placeholder="Type the address AP shipped to"
            value={form.delivery_address} onChange={(e) => setField('delivery_address', e.target.value)} />
        )}
        {addrStatus === 'wrong' ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '9px 12px', marginBottom: 16, background: 'rgba(239,68,68,.09)', border: '1px solid rgba(239,68,68,.45)', borderRadius: 8 }}>
            <AlertTriangle size={15} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: '.78rem', lineHeight: 1.5 }}>
              <b style={{ color: '#ef4444' }}>This is the wrong address.</b> Uniforms shipped here don't reach us.
              If the order is already in with AP, record it as it stands and chase the redirect — otherwise change
              it on the AP portal to {CORRECT_ADDRESS}.
            </div>
          </div>
        ) : addrStatus === 'unknown' ? (
          <div style={{ fontSize: '.72rem', color: '#d97706', marginBottom: 16 }}>
            Not one of the two known addresses — check it before saving.
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
            <MapPin size={12} /> AP's portal offers two ship-to addresses; only this one is ours.
          </div>
        )}

        {priorSizes.length > 0 && (
          <div style={{ padding: '10px 14px', marginBottom: 14, background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8 }}>
            <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--hp-gold)', marginBottom: 6 }}>SIZES ON FILE</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: '.78rem' }}>
              {priorSizes.map(([product, s]) => (
                <span key={product}>{product} <b style={{ fontFamily: 'var(--font-mono)' }}>{s.size}</b>
                  <span style={{ color: 'var(--hp-dim)' }}> ({fmtDate(s.when)})</span></span>
              ))}
            </div>
          </div>
        )}

        <label style={labelStyle}>Items</label>
        <datalist id="uniform-products">{cat.map((c) => <option key={c.product} value={c.product} />)}</datalist>
        {form.items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 56px 84px 30px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <input className="form-input" style={inputStyle} list="uniform-products" placeholder="Product (e.g. Cargo Shorts Mas Olive)"
              value={it.product} onChange={(e) => pickProduct(i, e.target.value)} />
            <input className="form-input" style={inputStyle} placeholder="Prod ID" value={it.product_id} onChange={(e) => setItem(i, 'product_id', e.target.value)} />
            <input className="form-input" style={inputStyle} placeholder="Size" value={it.size} onChange={(e) => setItem(i, 'size', e.target.value)} />
            <input className="form-input" style={inputStyle} type="number" min="1" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} />
            <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} />
            <button className="btn btn-ghost btn-sm" onClick={() => dropItem(i)} disabled={form.items.length === 1} title="Remove this line"><X size={13} /></button>
          </div>
        ))}
        {resized.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, padding: '8px 11px', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.35)', borderRadius: 7 }}>
            <AlertTriangle size={14} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: '.75rem', lineHeight: 1.5 }}>
              Size changed on <b>{resized.join(', ')}</b> — AP uses a different Product ID for each size,
              so the ID carried over from the last order is now the wrong one. Take it from the AP catalogue, or clear it.
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={addItem}><Plus size={13} /> Add item</button>
          <div style={{ fontSize: '.9rem' }}>Order total <b>{money(total)}</b></div>
        </div>

        <label style={labelStyle}>Note</label>
        <textarea className="form-input" style={{ ...inputStyle, minHeight: 70 }} placeholder="Anything worth remembering — a replacement for damaged kit, a size that came up wrong…"
          value={form.note} onChange={(e) => setField('note', e.target.value)} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: '.72rem', color: 'var(--hp-dim)' }}>
          <CalendarDays size={12} /> {form.staff_type === 'sorter'
            ? 'Night sort staff are kept in this register only — there are no uniform fields on a sorter record.'
            : "Saving updates this driver's Personnel profile with the latest order date and reference."}
        </div>
      </div>
    </div>
  );
}
